import { Grid } from '@kerfjs/ui/grid';
import type { SafeHtml } from 'kerfjs';

import type { AnalysisSummaryT } from '../../../../../../shared/contract/analysis.js';
import type { AnalysisReport } from '../../../../../data/api.js';
import { KPI_TILE_MIN_WIDTH, KpiTile } from '../../../../kpi-tile.js';

/** Headline values and verdict counts from the selected pricing check. */
export function OverviewSummarySection({
  summary,
  verdicts,
}: {
  summary: AnalysisSummaryT;
  verdicts: AnalysisReport['verdicts'];
}): SafeHtml {
  const counts = { high: 0, slightlyHigh: 0, fair: 0 };
  for (const verdict of verdicts) {
    if (verdict.verdict === 'high' || verdict.verdict === 'very-high') counts.high += 1;
    else if (verdict.verdict === 'slightly-high') counts.slightlyHigh += 1;
    else if (verdict.verdict === 'fair' || verdict.verdict === 'below-market') counts.fair += 1;
  }
  // No line has a quote yet: show what quotes should come back at, not a comparison (HS2-AX3JJY).
  if (summary.expected !== undefined && summary.totalAtBestQuotes === 0) {
    return (
      <Grid
        minColumnWidth={KPI_TILE_MIN_WIDTH}
        gap={{ column: 'none', row: 's' }}
        className="overview-summary-section summary-strip"
        rootAttributes={{ 'data-mode': 'estimate' }}
      >
        <KpiTile amount={summary.expected.midpoint} currency={summary.base} label="Expected Total" />
        <KpiTile amount={summary.expected.low} currency={summary.base} label="Market Low" />
        <KpiTile amount={summary.expected.fairUpTo} currency={summary.base} label="Fair Quotes Up To" />
        <KpiTile value={String(summary.estimateCount ?? 0)} label="Lines Estimated" />
        <KpiTile value={String(summary.insufficientCount)} label="Insufficient Data" />
      </Grid>
    );
  }
  // Overpayment is over quoted lines only; older runs lack that base and use the full midpoint total.
  const overBase = summary.quotedAtBenchmarkMidpoint ?? summary.totalAtBenchmarkMidpoint;
  const overPct = overBase !== 0 ? summary.estimatedOverpayment / overBase : null;
  const overpaymentLabel =
    overPct === null
      ? 'Estimated overpayment'
      : `Estimated overpayment (${new Intl.NumberFormat('en-PK', { style: 'percent', signDisplay: 'exceptZero' }).format(overPct)})`;
  return (
    <Grid
      minColumnWidth={KPI_TILE_MIN_WIDTH}
      gap={{ column: 'none', row: 's' }}
      className="overview-summary-section summary-strip"
    >
      <KpiTile amount={summary.totalAtBestQuotes} currency={summary.base} label="At Best Quotes" />
      <KpiTile amount={summary.totalAtBenchmarkMidpoint} currency={summary.base} label="At Benchmark Landed" />
      <KpiTile amount={summary.estimatedOverpayment} currency={summary.base} label={overpaymentLabel} />
      <KpiTile
        value={`${String(counts.high)} · ${String(counts.slightlyHigh)} · ${String(counts.fair)}`}
        label="High · Slightly High · Fair"
      />
      <KpiTile value={String(summary.insufficientCount)} label="Insufficient Data" />
      {(summary.estimateCount ?? 0) > 0 ? <KpiTile value={String(summary.estimateCount)} label="No Quote Yet" /> : null}
    </Grid>
  );
}
